import {
  Input_V2_Mutations_Equipment_CreateEquipmentMutationInput,
  Input_V2_Mutations_Equipment_UpdateEquipmentMutationInput
} from '@/common/infrastructure/gql/generated/schema';
import { EquipmentDetails, EquipmentFormData, EquipmentListItemDto, EquipmentTableData } from '../types/EquipmentTypes';
import { mapNamedEntity } from '../utils/mapNamedEntity';

/**
 * Maps backend status values to proper display format
 */
export const mapEquipmentStatus = (statusName: string | null | undefined): string => {
  if (!statusName) return 'N/A';

  switch (statusName.toLowerCase()) {
    case 'inservice':
      return 'In Service';
    case 'outofservice':
      return 'Out of Service';
    case 'inactive':
      return 'Inactive';
    case 'available':
      return 'Available';
    case 'maintenance':
      return 'Maintenance';
    case 'booked':
      return 'Booked';
    case 'unspecified':
      return 'Unspecified';
    default:
      return statusName;
  }
};

export const mapEquipmentToFormData = (equipment?: EquipmentDetails | null): EquipmentFormData => {
  if (!equipment) {
    return {
      id: undefined,
      name: '',
      makeId: undefined,
      makeName: '',
      modelId: undefined,
      modelName: '',
      year: undefined,
      classId: undefined,
      typeId: undefined,
      sizeId: undefined,
      statusId: undefined,
      defaultEmployeeId: undefined,
      defaultCrewRoleId: undefined,
      dispatchLocationId: undefined,
      licensePlateState: '',
      licensePlateNumber: '',
      defaultLocationId: undefined,
      locationId: undefined,
      gpsTrackerSerialNumber: '',
      geofencingEnabled: false,
      notes: ''
    };
  }

  return {
    id: equipment.id ?? undefined,
    name: equipment.name ?? '',
    makeId: equipment.make?.id ?? undefined,
    makeName: equipment.make?.name ?? '',
    modelId: equipment.model?.id ?? undefined,
    modelName: equipment.model?.name ?? '',
    year: equipment.year ? new Date(equipment.year, 0) : undefined,
    classId: equipment.class?.id ?? undefined,
    typeId: equipment.type?.id ?? undefined,
    sizeId: equipment.size?.id ?? undefined,
    statusId: equipment.status?.id ?? undefined,
    defaultEmployeeId: equipment.defaultEmployee?.id ?? undefined,
    defaultCrewRoleId: equipment.defaultEmployee?.defaultEmployeeCrewRoleId ?? undefined, // Use defaultEmployeeCrewRoleId from the default employee
    dispatchLocationId: equipment.dispatchLocation?.id ?? undefined,
    licensePlateState: equipment.licensePlate?.state ?? '',
    licensePlateNumber: equipment.licensePlate?.number ?? '',
    defaultLocationId: equipment.defaultLocation?.id ?? undefined,
    locationId: equipment.location?.id ?? undefined,
    gpsTrackerSerialNumber: equipment.gpsTrackerSerialNumber ?? '',
    geofencingEnabled: equipment.geoFencingEnabled ?? false,
    notes: equipment.notes ?? ''
  };
};

export const mapEquipmentToTableData = (equipment: EquipmentListItemDto): EquipmentTableData => {
  return {
    id: equipment.id,
    deletable: equipment.deletable ?? false,
    name: equipment.name ?? 'N/A',
    description: equipment.notes ?? '',
    make: mapNamedEntity(equipment.make),
    model: mapNamedEntity(equipment.model),
    year: equipment.year ?? 0,
    class: mapNamedEntity(equipment.class),
    type: mapNamedEntity(equipment.type),
    size: mapNamedEntity(equipment.size),
    status: {
      id: equipment.status?.id ?? 0,
      name: mapEquipmentStatus(equipment.status?.name ?? 'N/A')
    },
    upcomingOutOfService: (equipment.upcomingOutOfService ?? []) as EquipmentTableData['upcomingOutOfService'],
    vinNumber: equipment.vinNumber ?? '',
    gpsTrackerSerialNumber: equipment.gpsTrackerSerialNumber ?? '',
    geoFencingEnabled: equipment.geoFencingEnabled ?? false,
    licensePlateNumber: equipment.licensePlate?.number ?? '',
    licensePlateState: equipment.licensePlate?.state ?? '',
    grossWeight: equipment.grossWeight ?? null,
    acquiredOn: equipment.acquiredOn ?? null,
    beganServiceOn: equipment.beganServiceOn ?? null,
    purchasedMileage: equipment.purchasedMileage ?? null,
    beganServiceMileage: equipment.beganServiceMileage ?? null,
    homeYard: equipment.defaultLocation?.name ?? '',
    currentYard: equipment.location?.name ?? '',
    dispatchLocationName: equipment.dispatchLocation?.name ?? '',
    mpEquipmentId: equipment.mpEquipmentId ?? null,
    truckYear: equipment.truckYear ?? null,
    truckModelNumber: equipment.truckModelNumber ?? '',
    machineSerialNumber: equipment.machineSerialNumber ?? '',
    boomSerialNumber: equipment.boomSerialNumber ?? '',
    defaultInsideSystemDiameter: equipment.defaultInsideSystemDiameter ?? null,
    defaultEndStyle: equipment.defaultEndStyle ?? null,
    maxGroundBearingPressure: equipment.maxGroundBearingPressure ?? null,
    daysDown: equipment.daysDown ?? null,
    boomModelNum: equipment.boomModelNum ?? '',
    currentUnWorkable: equipment.unworkable ?? null
  };
};

export const mapFormDataToCreateInput = (formData: EquipmentFormData): Input_V2_Mutations_Equipment_CreateEquipmentMutationInput => {
  // Check for null/undefined only - 0 is a valid ID
  if (formData.classId == null || formData.typeId == null || formData.sizeId == null) {
    throw new Error('Class, Type, and Size are required fields for creating equipment');
  }

  return {
    name: formData.name ?? '',
    ...(formData.makeId != null && { makeId: formData.makeId }),
    notes: formData.notes ?? '',
    ...(formData.makeName && { makeName: formData.makeName }),
    ...(formData.modelId != null && { modelId: formData.modelId }),
    ...(formData.modelName && { modelName: formData.modelName }),
    ...(formData.year && { year: formData.year instanceof Date ? formData.year.getFullYear() : formData.year }),
    classId: formData.classId,
    typeId: formData.typeId,
    sizeId: formData.sizeId,
    ...(formData.statusId != null && { statusId: formData.statusId }),
    ...(formData.defaultEmployeeId != null && { defaultEmployeeId: formData.defaultEmployeeId }),
    ...(formData.defaultCrewRoleId != null && { defaultCrewRoleId: formData.defaultCrewRoleId }),
    ...(formData.dispatchLocationId != null && { dispatchLocationId: formData.dispatchLocationId }),
    ...(formData.licensePlateState && { licensePlateState: formData.licensePlateState }),
    ...(formData.licensePlateNumber && { licensePlateNumber: formData.licensePlateNumber }),
    ...(formData.defaultLocationId != null && { defaultLocationId: formData.defaultLocationId }),
    ...(formData.locationId != null && { locationId: formData.locationId }),
    ...(formData.gpsTrackerSerialNumber && { gpsTrackerSerialNumber: formData.gpsTrackerSerialNumber }),
    ...(formData.geofencingEnabled !== undefined && { geofencingEnabled: formData.geofencingEnabled }),
    ...(formData.notes && { notes: formData.notes })
  };
};

export const mapFormDataToUpdateInput = (formData: EquipmentFormData): Input_V2_Mutations_Equipment_UpdateEquipmentMutationInput => {
  if (!formData.id) {
    throw new Error('Equipment ID is required for update');
  }

  // Validate required fields for update - 0 is a valid ID
  if (formData.classId == null || formData.typeId == null || formData.sizeId == null) {
    throw new Error('Class, Type, and Size are required fields for updating equipment');
  }

  // Helper function to handle clearable string fields
  const mapClearableStringField = (value: string | null | undefined): string | null => {
    if (value === null || value === undefined || value.trim() === '') {
      return null;
    }
    return value.trim();
  };

  // Helper function to handle clearable numeric/ID fields
  // Handles both number and string types (string can come from FormikSelect)
  // Note: 0 is a valid ID (e.g., statusId for "In Service"), so we only treat null/undefined/empty as cleared
  const mapClearableIdField = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'string') {
      const numValue = Number(value);

      if (isNaN(numValue)) {
        return null;
      }
      return numValue;
    }

    return value;
  };

  return {
    id: formData.id,
    name: formData.name || '',
    classId: formData.classId,
    typeId: formData.typeId,
    sizeId: formData.sizeId,
    // Handle clearable ID fields - send null when cleared
    ...(formData.makeId !== undefined && { makeId: mapClearableIdField(formData.makeId) }),
    ...(formData.makeName !== undefined && { makeName: mapClearableStringField(formData.makeName) }),
    ...(formData.modelId !== undefined && { modelId: mapClearableIdField(formData.modelId) }),
    ...(formData.modelName !== undefined && { modelName: mapClearableStringField(formData.modelName) }),
    ...(formData.year !== undefined && {
      year: formData.year ? (formData.year instanceof Date ? formData.year.getFullYear() : formData.year) : null
    }),
    ...(formData.statusId !== undefined && { statusId: mapClearableIdField(formData.statusId) }),
    ...(formData.defaultEmployeeId !== undefined && { defaultEmployeeId: mapClearableIdField(formData.defaultEmployeeId) }),
    ...(formData.defaultCrewRoleId !== undefined && { defaultCrewRoleId: mapClearableIdField(formData.defaultCrewRoleId) }),
    ...(formData.dispatchLocationId !== undefined && { dispatchLocationId: mapClearableIdField(formData.dispatchLocationId) }),
    ...(formData.licensePlateState !== undefined && { licensePlateState: mapClearableStringField(formData.licensePlateState) }),
    ...(formData.licensePlateNumber !== undefined && { licensePlateNumber: mapClearableStringField(formData.licensePlateNumber) }),
    ...(formData.defaultLocationId !== undefined && { defaultLocationId: mapClearableIdField(formData.defaultLocationId) }),
    ...(formData.locationId !== undefined && { locationId: mapClearableIdField(formData.locationId) }),
    ...(formData.gpsTrackerSerialNumber !== undefined && {
      gpsTrackerSerialNumber: mapClearableStringField(formData.gpsTrackerSerialNumber)
    }),
    ...(formData.geofencingEnabled !== undefined && { geofencingEnabled: formData.geofencingEnabled }),
    ...(formData.notes !== undefined && { notes: mapClearableStringField(formData.notes) }),
    ...(formData.outOfServicesToCreate?.length && {
      outOfServicesToCreate: formData.outOfServicesToCreate.map((oos) => ({
        equipment: { id: oos.equipment!.id, inService: 1 },
        start: oos.start,
        end: oos.end,
        reason: { id: oos.reason.id, name: oos.reason.name ?? '' },
        notes: oos.notes ?? ''
      }))
    }),
    ...(formData.outOfServicesToUpdate?.length && {
      outOfServicesToUpdate: formData.outOfServicesToUpdate.map((oos) => ({
        id: oos.id,
        equipment: oos.equipment ? { id: oos.equipment.id, inService: 1 } : { id: formData.id!, inService: 1 },
        start: oos.start,
        end: oos.end,
        reason: { id: oos.reason.id, name: oos.reason.name ?? '' },
        notes: oos.notes ?? ''
      }))
    }),
    ...(formData.outOfServicesToDelete?.length && {
      outOfServicesToDelete: formData.outOfServicesToDelete
    })
  } as Input_V2_Mutations_Equipment_UpdateEquipmentMutationInput;
};
